import { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  HStack,
  VStack,
  Text,
  Box,
} from '@chakra-ui/react'

interface RateModalProps {
  isOpen: boolean
  onClose: () => void
  beerName: string
  brewery?: string
  meetingDateDisplay: string
  initialScore?: number
  initialNotes?: string
  saving: boolean
  onSave: (score: number, notes: string) => void
  onClear?: () => void
}

export default function RateModal({
  isOpen, onClose, beerName, brewery, meetingDateDisplay,
  initialScore, initialNotes, saving, onSave, onClear,
}: RateModalProps) {
  const [score, setScore] = useState(initialScore ?? 0)
  const [notes, setNotes] = useState(initialNotes ?? '')

  useEffect(() => {
    if (isOpen) {
      setScore(initialScore ?? 0)
      setNotes(initialNotes ?? '')
    }
  }, [isOpen, initialScore, initialNotes])

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered returnFocusOnClose={false}>
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader pb={2}>
          <VStack align="start" spacing={0}>
            <Text>{beerName}</Text>
            {brewery && <Text fontSize="sm" color="whiteAlpha.500" fontWeight="normal">{brewery}</Text>}
            <Text fontSize="xs" color="whiteAlpha.400" fontWeight="normal">{meetingDateDisplay}</Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            {/* Score display */}
            <Text fontSize="3xl" fontWeight="bold" color="orange.300">
              {score > 0 ? `★ ${score.toFixed(2).replace(/\.?0+$/, '')}` : '—'}
            </Text>
            {/* Slider */}
            <Box w="100%" px={2}>
              <Slider
                value={score}
                onChange={v => setScore(Math.round(v * 4) / 4)}
                min={0} max={5} step={0.25}
                focusThumbOnChange={false}
              >
                <SliderTrack bg="whiteAlpha.200" h="8px" borderRadius="full">
                  <SliderFilledTrack bg="orange.400" />
                </SliderTrack>
                <SliderThumb boxSize={6} bg="orange.300" _focus={{ boxShadow: '0 0 0 3px rgba(237,137,54,0.4)' }} />
              </Slider>
              <HStack justify="space-between" mt={1}>
                <Text fontSize="xs" color="whiteAlpha.400">0</Text>
                <Text fontSize="xs" color="whiteAlpha.400">1</Text>
                <Text fontSize="xs" color="whiteAlpha.400">2</Text>
                <Text fontSize="xs" color="whiteAlpha.400">3</Text>
                <Text fontSize="xs" color="whiteAlpha.400">4</Text>
                <Text fontSize="xs" color="whiteAlpha.400">5</Text>
              </HStack>
            </Box>
            {/* Notes */}
            <FormControl>
              <FormLabel fontSize="sm">Notes (optional)</FormLabel>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Tasting notes..."
                rows={3}
                borderColor="whiteAlpha.300"
                _placeholder={{ color: 'whiteAlpha.400' }}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          {onClear && initialScore != null && initialScore > 0 && (
            <Button variant="ghost" colorScheme="red" mr="auto" onClick={onClear} isLoading={saving}>
              Clear
            </Button>
          )}
          <Button variant="ghost" mr={3} onClick={onClose} color="whiteAlpha.700">Cancel</Button>
          <Button colorScheme="orange" onClick={() => onSave(score, notes)} isLoading={saving} isDisabled={score === 0}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
